import React, { useEffect, useState } from "react";
import { Send } from "lucide-react";
import TextInput from "../../ui/TextInput";
import Dropdown from "../../ui/SelectDropdown";
import TextArea from "../../ui/TextArea";
import { useAppDispatch, useAppSelector } from "../../../redux/app/hooks";
import { RootState } from "../../../redux/app/store";
import { cleanObject } from "../../../helpers";
import {
  clearOrders,
  fetchAllOrders,
} from "../../../redux/features/orderSlice";
import {
  sendSingleNotification,
  sendBulkNotification,
} from "../../../redux/features/notificationSlice";

const targetUserTypes = [
  { label: "BOTH", value: "BOTH" },
  { label: "VENDOR", value: "VENDOR" },
  { label: "BUYER", value: "BUYER" },
];

const PushNotificationsPage = () => {
  const { orders } = useAppSelector((state: RootState) => state.orders);
  const [refinedOrders, setRefinedOrders] = useState<any[]>([]);
  const [filter, setFilter] = useState("");
  const [singlePayload, setSinglePayload] = useState({
    title: "",
    contractId: "",
    text: "",
    notificationType: "INFO", // fixed
  });
  const [bulkPayload, setBulkPayload] = useState({
    title: "",
    message: "",
    targetUserType: "ALL",
    notificationType: "INFO", // fixed
  });
  const { sendingSingle, sendingBulk } = useAppSelector(
    (state: RootState) => state.notifications
  );
  const dispatch = useAppDispatch();

  const handleSingleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await dispatch(
        sendSingleNotification(cleanObject(singlePayload))
      ).unwrap();
      // reset form after success
      setSinglePayload({
        title: "",
        contractId: "",
        text: "",
        notificationType: "INFO",
      });
    } catch (err) {
      console.error("Failed to send single notification", err);
    }
  };

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await dispatch(sendBulkNotification(cleanObject(bulkPayload))).unwrap();
      // reset form after success
      setBulkPayload({
        title: "",
        message: "",
        targetUserType: "ALL",
        notificationType: "INFO",
      });
    } catch (err) {
      console.error("Failed to send bulk notification", err);
    }
  };

  useEffect(() => {
    const filteredOrders: any = orders?.data?.data?.map((x) => ({
      label: `${x?.contract?.title} (${x?.contract?.contractCode})`,
      value: {
        orderId: x?.id,
        contractId: x?.contract?.id,
      },
    }));
    setRefinedOrders(filteredOrders);
  }, [orders]);

  useEffect(() => {
    const params = cleanObject({
      limit: 10,
      page: 1,
      filter,
    });
    dispatch(clearOrders());
    dispatch(fetchAllOrders(params));
  }, [dispatch, filter]);

  return (
    <div className="p-6 space-y-10">
      {/* Page Title */}
      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-2xl font-bold">Push Notifications</h1>
      </div>

      {/* Single Notification Section */}
      <form
        onSubmit={handleSingleSubmit}
        className="bg-white shadow-lg rounded-2xl p-6 space-y-4"
      >
        <h2 className="text-lg font-semibold mb-4">Single Notification</h2>

        <TextInput
          id="single-title"
          type="text"
          placeholder="Title"
          value={singlePayload.title}
          register={{
            onChange: (e: any) =>
              setSinglePayload({ ...singlePayload, title: e.target.value }),
          }}
        />

        <Dropdown
          label="Contract"
          placeholder="Search by code"
          options={refinedOrders}
          value={singlePayload.contractId}
          onSelect={(val: any) =>
            setSinglePayload({ ...singlePayload, contractId: val?.contractId })
          }
          onSearch={(query) => setFilter(query)}
        />

        <TextArea
          id="single-text"
          placeholder="Notification text"
          value={singlePayload.text}
          onChange={(e) =>
            setSinglePayload({ ...singlePayload, text: e.target.value })
          }
        />

        <button
          type="submit"
          disabled={sendingSingle}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow text-white ${
            sendingSingle
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-primary hover:bg-primary/90"
          }`}
        >
          {sendingSingle ? (
            <>
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              Sending...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Send Notification
            </>
          )}
        </button>
      </form>

      {/* Bulk Notification Section */}
      <form
        onSubmit={handleBulkSubmit}
        className="bg-white shadow-lg rounded-2xl p-6 space-y-4"
      >
        <h2 className="text-lg font-semibold mb-4">Bulk Notification</h2>

        <TextInput
          id="bulk-title"
          type="text"
          placeholder="Title"
          value={bulkPayload.title}
          register={{
            onChange: (e: any) =>
              setBulkPayload({ ...bulkPayload, title: e.target.value }),
          }}
        />

        <TextArea
          id="bulk-message"
          placeholder="Message"
          value={bulkPayload.message}
          onChange={(e) =>
            setBulkPayload({ ...bulkPayload, message: e.target.value })
          }
        />

        <Dropdown
          label="Target User Type"
          options={targetUserTypes}
          value={bulkPayload.targetUserType}
          onSelect={(val) =>
            setBulkPayload({ ...bulkPayload, targetUserType: val })
          }
        />

        <button
          type="submit"
          disabled={sendingBulk}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg shadow text-white ${
            sendingBulk
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-primary hover:bg-primary/90"
          }`}
        >
          {sendingBulk ? (
            <>
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
              Sending...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Send Bulk Notification
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default PushNotificationsPage;
